import * as RadixUiAlertDialog from "@radix-ui/react-alert-dialog";
import classNames from "classnames";
import React from "react";

import { PageDataLayer, tracking } from "../../app/services/tracking";
import { Markdown } from "./Markdown";
import { Overlay } from "./Overlay";

import AccountDesktopImage from "./AlertDialogAccountDesktopImage.jpg";
import AccountMobileImage from "./AlertDialogAccountMobileImage.jpg";
import ContractsDesktopImage from "./AlertDialogContractsDesktopImage.jpg";
import ContractsMobileImage from "./AlertDialogContractsMobileImage.jpg";
import InformationDesktopImage from "./AlertDialogInformationDesktopImage.jpg";
import InformationMobileImage from "./AlertDialogInformationMobileImage.jpg";

const classNamePrefix = "AlertDialog";

type AlertDialogRootProps = RadixUiAlertDialog.AlertDialogProps;

const AlertDialogRoot = (props: AlertDialogRootProps) => (
  <RadixUiAlertDialog.Root {...props} />
);

type AlertDialogContentProps = Omit<
  RadixUiAlertDialog.AlertDialogContentProps,
  "title"
> & {
  action?: React.ReactNode;
  cancel?: React.ReactNode;
  dataLayer?: PageDataLayer;
  icon?: React.ReactNode;
  scrollRef?: React.ForwardedRef<HTMLDivElement>;
  text: React.ReactNode;
  title: React.ReactNode;
  variant: "account" | "contracts" | "information";
};

const AlertDialogContent = React.forwardRef(
  (
    props: AlertDialogContentProps,
    ref?: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const {
      action,
      cancel,
      children,
      className,
      dataLayer,
      icon,
      scrollRef,
      text,
      title,
      variant,
      onOpenAutoFocus,
      onScroll,
      ...restProps
    } = props;

    const image = {
      account: { desktop: AccountDesktopImage, mobile: AccountMobileImage },
      contracts: {
        desktop: ContractsDesktopImage,
        mobile: ContractsMobileImage,
      },
      information: {
        desktop: InformationDesktopImage,
        mobile: InformationMobileImage,
      },
    }[variant];

    return (
      <RadixUiAlertDialog.Portal>
        <RadixUiAlertDialog.Overlay asChild>
          <Overlay />
        </RadixUiAlertDialog.Overlay>
        <RadixUiAlertDialog.Content
          ref={ref}
          {...restProps}
          className={classNames(className, classNamePrefix)}
          data-icon={icon ? true : undefined}
          data-image={image ? true : undefined}
          data-variant={variant}
          onOpenAutoFocus={(event) => {
            onOpenAutoFocus?.(event);

            if (dataLayer) {
              tracking.trackPage(dataLayer);
            }
          }}
        >
          {image && (
            <div className={`${classNamePrefix}-image`}>
              <picture>
                <source media="(min-width: 960px)" srcSet={image.desktop} />
                <img alt="" src={image.mobile} />
              </picture>
            </div>
          )}
          <div className={`${classNamePrefix}-content`}>
            <div className={`${classNamePrefix}-header`}>
              {icon && <div className={`${classNamePrefix}-icon`}>{icon}</div>}
              <RadixUiAlertDialog.Title asChild>
                <h1 className={`${classNamePrefix}-title`}>
                  <Markdown>{title}</Markdown>
                </h1>
              </RadixUiAlertDialog.Title>
            </div>
            <RadixUiAlertDialog.Description asChild>
              <div
                ref={scrollRef}
                className={`${classNamePrefix}-body`}
                onScroll={onScroll}
              >
                <Markdown>{text}</Markdown>
                {children}
              </div>
            </RadixUiAlertDialog.Description>
            {(action || cancel) && (
              <div className={`${classNamePrefix}-footer`}>
                {cancel && (
                  <RadixUiAlertDialog.Cancel asChild>
                    {cancel}
                  </RadixUiAlertDialog.Cancel>
                )}
                {action}
              </div>
            )}
          </div>
        </RadixUiAlertDialog.Content>
      </RadixUiAlertDialog.Portal>
    );
  },
);

type AlertDialogTriggerProps = RadixUiAlertDialog.AlertDialogTriggerProps;

const AlertDialogTrigger = React.forwardRef(
  (
    props: AlertDialogTriggerProps,
    ref?: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    const { asChild = true, ...restProps } = props;
    return (
      <RadixUiAlertDialog.Trigger ref={ref} {...restProps} asChild={asChild} />
    );
  },
);

export const AlertDialog = Object.assign(AlertDialogRoot, {
  Content: AlertDialogContent,
  Trigger: AlertDialogTrigger,
});
